import { EMAIL_PLATFORMS } from '@this+that/constants';
import {
  LabelId,
  MessagingPlatform,
  UserCommunication,
  UserCommunicationRemoveBatchPayload,
  UserCommunicationRemovePayload,
  WebSocketAction,
  WebSocketEntityType,
} from '@this+that/models';
import log from 'loglevel';

import { IEventEmitter } from '../EventEmitter';
import { WebSocketEventType } from '../WebSocketContext';

type UserCommunicationMessageItem =
  | UserCommunication
  | UserCommunicationRemovePayload
  | UserCommunicationRemoveBatchPayload;

const ACCOUNT_GATED_PLATFORMS: ReadonlySet<MessagingPlatform> = new Set(
  EMAIL_PLATFORMS,
);

// INSERT reloads the thread with decrypt: true, so userEmailAddress is the only
// action where this field is plaintext — 'in' narrows without casting to UserCommunication.
function isFromRemovedAccount(
  item: UserCommunication | UserCommunicationRemovePayload,
  isEmailAccountRemoved: (
    platform: MessagingPlatform,
    address: string,
  ) => boolean,
): boolean {
  if (!item.platform || !ACCOUNT_GATED_PLATFORMS.has(item.platform)) {
    return false;
  }

  const userEmailAddress =
    'userEmailAddress' in item ? item.userEmailAddress : undefined;
  if (!userEmailAddress) {
    return false;
  }

  return isEmailAccountRemoved(item.platform, userEmailAddress);
}

function isDraftUserCommunication(
  item: UserCommunication | UserCommunicationRemovePayload,
): boolean {
  return Boolean(item.labelIds?.includes(LabelId.DRAFT) || item.draftId);
}

function emitRemoveBatch(
  eventEmitter: IEventEmitter,
  batch: UserCommunicationRemoveBatchPayload,
): void {
  const nonDraftItems = batch.items.filter(
    (item) => !isDraftUserCommunication(item),
  );

  if (nonDraftItems.length === 0) {
    log.debug(
      '[UserCommunicationHandler] Ignoring REMOVE_BATCH with only draft items',
    );
    return;
  }

  log.debug(
    `[UserCommunicationHandler] REMOVE_BATCH UserCommunication (${nonDraftItems.length} items)`,
  );
  eventEmitter.emit(
    WebSocketEntityType.UserCommunication,
    WebSocketEventType.REMOVE_BATCH,
    {
      ...batch,
      items: nonDraftItems,
    },
  );
}

/**
 * Creates a handler for UserCommunication WebSocket messages
 *
 * @param eventEmitter - Event emitter to dispatch events to subscribers
 * @param isEmailAccountRemoved - Checks whether a GMAIL/OUTLOOK address is currently blocklisted
 * @returns Handler function for UserCommunication messages
 */
export function createUserCommunicationMessageHandler(
  eventEmitter: IEventEmitter,
  isEmailAccountRemoved: (
    platform: MessagingPlatform,
    address: string,
  ) => boolean,
) {
  return (action: WebSocketAction, item: UserCommunicationMessageItem) => {
    try {
      if (action === WebSocketAction.REMOVE_BATCH) {
        emitRemoveBatch(
          eventEmitter,
          item as UserCommunicationRemoveBatchPayload,
        );
        return;
      }

      const singleItem = item as
        | UserCommunication
        | UserCommunicationRemovePayload;

      // Filter: Ignore DRAFT communications to prevent state conflicts with local editing
      if (isDraftUserCommunication(singleItem)) {
        log.debug(
          `[UserCommunicationHandler] Ignoring DRAFT communication to prevent local editing conflicts: ${singleItem.messageId}`,
        );
        return;
      }

      switch (action) {
        case WebSocketAction.INSERT: {
          if (isFromRemovedAccount(singleItem, isEmailAccountRemoved)) {
            log.debug(
              `[UserCommunicationHandler] Ignoring INSERT for removed account: ${singleItem.messageId}`,
            );
            return;
          }
          log.debug(
            `[UserCommunicationHandler] INSERT UserCommunication ${singleItem.messageId}`,
          );
          eventEmitter.emit(
            WebSocketEntityType.UserCommunication,
            WebSocketEventType.INSERT,
            singleItem,
          );
          break;
        }
        case WebSocketAction.MODIFY:
          log.debug(
            `[UserCommunicationHandler] MODIFY UserCommunication ${singleItem.messageId}`,
          );
          eventEmitter.emit(
            WebSocketEntityType.UserCommunication,
            WebSocketEventType.MODIFY,
            singleItem,
          );
          break;
        case WebSocketAction.REMOVE:
          log.debug(
            `[UserCommunicationHandler] REMOVE UserCommunication ${singleItem.messageId}`,
          );
          eventEmitter.emit(
            WebSocketEntityType.UserCommunication,
            WebSocketEventType.REMOVE,
            singleItem,
          );
          break;
        default:
          log.warn('[UserCommunicationHandler] Unknown action:', action);
      }
    } catch (error) {
      log.error('[UserCommunicationHandler] Handler failed:', error);
    }
  };
}
